// ============================================================
// LegacyAdapter — backward-compatible wrapper for old GGPlayer API
// Maps old `init/play/stop/onStart/onStop/onViewers/appendUserData`
// to GGPlayer v4 API
// ============================================================
import type { PlayerConfig, ApiData, UserData } from '../core/types';
import { GGPlayer } from '../GGPlayer';
import { AnalyticsPlugin } from '../plugins/AnalyticsPlugin';
import { ClipsPlugin } from '../plugins/clips/ClipsPlugin';
import { TheatrePlugin } from '../plugins/theatre/TheatrePlugin';
import { ApiResolverPlugin } from '../plugins/ApiResolverPlugin';
import { WebSocketPlugin } from "@/plugins/WebSocketPlugin";
import { ReydenPlugin } from '@/index.mobile';
import { PlayerInWindowPlugin } from '../plugins/player-in-window/PlayerInWindowPlugin';

export interface LegacyInitData {
  src?: string;
  streamkey?: string;
  container?: HTMLElement;
  apidata?: ApiData;
  autoplay?: boolean | number;
  muted?: boolean | number;
  volume?: number;
  previewmode?: boolean | number;
  beta?: boolean;
  quality?: string;
}

/**
 * Drop-in replacement for old `new GGPlayer(initData)`.
 *
 * Usage:
 *   const player = new GGPlayerLegacy(initData);
 *   // Same API as before: player.play(), player.onStart(cb), etc.
 */
export class GGPlayerLegacy {
  private player: GGPlayer | null = null;
  private _onStartCb: (() => void) | null = null;
  private _onStopCb: (() => void) | null = null;
  private _removedCheckInterval: ReturnType<typeof setInterval> | null = null;
  private apiResolverPlugin: ApiResolverPlugin | null = null;

  constructor(initData?: LegacyInitData) {
    if (initData) this.init(initData);
  }

  init(data: LegacyInitData) {
    const container = data.container;
    if (!container) throw new Error('container is required');

    const streamKey = data.src || data.streamkey || '';
    const isPreview = !!(data.previewmode);
    const plugins = [];

    if (!isPreview) {
      plugins.push(new AnalyticsPlugin());
      plugins.push(new ClipsPlugin());
      plugins.push(new TheatrePlugin());
      plugins.push(new WebSocketPlugin());
      plugins.push(new ReydenPlugin());
      plugins.push(new PlayerInWindowPlugin());

      this.apiResolverPlugin = new ApiResolverPlugin();
      plugins.push(this.apiResolverPlugin);
    }

    const config: PlayerConfig = {
      container,
      streamKey,
      autoplay: !!(data.autoplay),
      muted: data.muted !== undefined ? !!(data.muted) : undefined,
      volume: typeof data.volume === 'number' ? data.volume : undefined,
      quality: 'auto',
      previewMode: isPreview,
      apiData: data.apidata,
      plugins,
    };

    this.player = new GGPlayer(config);

    // Listen for start/stop
    this.player.on('playing', () => { this._onStartCb?.(); });
    this.player.on('stopped', () => { this._onStopCb?.(); });

    // Auto-remove check (like old player)
    this._removedCheckInterval = setInterval(() => {
      if (document.contains && !document.contains(container)) {
        this.destroy();
      }
    }, 1000);
  }

  play() {
    return this.player?.play();
  }

  stop() {
    return this.player?.stop();
  }

  /** Returns this adapter instance (the integration-facing legacy player). */
  getPlayer(): GGPlayerLegacy {
    return this;
  }

  /** Returns the underlying GGPlayer core instance (for internal/dev use). */
  getCorePlayer(): GGPlayer | null {
    return this.player;
  }

  destroy() {
    if (this._removedCheckInterval) {
      clearInterval(this._removedCheckInterval);
      this._removedCheckInterval = null;
    }
    if (this._onStopCb) {
      this._onStopCb();
      this._onStopCb = null;
    }
    this.player?.destroy();
    this.player = null;
  }

  detach() {
    // no-op in v4
  }

  clean() {
    return this.player?.clean();
  }

  isPlaying() {
    return this.player?.isPlaying ?? false;
  }

  onStart(cb: () => void) {
    this._onStartCb = cb;
  }

  onStop(cb: () => void) {
    this._onStopCb = cb;
  }

  onLogin() {
    // no-op — user data handled via apiData
  }

  onViewers(cb: (count: number) => void) {
    this.player?.on('viewers:update', cb);
  }

  appendUserData(user: UserData) {
    this.apiResolverPlugin?.appendUserData(user);
  }

  appendClipsStatus(status: boolean) {
    // Emit clips events via player's eventBus
    if (this.player) {
      if (status) {
        this.player.emit('clips:disabled');
      } else {
        this.player.emit('clips:available');
      }
    }
  }

  checkPremiumKey(
    _premiumKey: string,
    _streamKey: string,
    _id: string,
    _callback: Function,
  ) {
    // Premium not implemented in v4
  }

  startTestPremium() {
    // no-op
  }

  startCommercial(_key: string, _pwd: string) {
    // no-op
  }
}
